// src/Table.js
import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, getDocs, doc, updateDoc, setDoc } from "firebase/firestore";

const ADMIN_EMAILS = ["j.bankmann@redkorps.de", "r.schroeder@redkorps.de", "n.kieper@redkorps.de"];

const Table = ({ user }) => {
  const [members, setMembers] = useState([]);
  const [performances, setPerformances] = useState([]);
  const [newPerformanceName, setNewPerformanceName] = useState(""); // State für den neuen Auftrittsnamen
  const [newPerformanceDate, setNewPerformanceDate] = useState(""); // State für das Datum

  const updateAvailability = async (memberId, performanceId, status) => {
    const memberRef = doc(db, "members", memberId);
    await updateDoc(memberRef, {
      [performanceId]: status,
    });

    setMembers((prev) =>
      prev.map((m) =>
        m.id === memberId ? { ...m, [performanceId]: status } : m
      )
    );
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" }); // Abkürzung des Monats
    const year = String(date.getFullYear()).slice(-2); // Letzte zwei Ziffern des Jahres
  
    return `${day}. ${month} '${year}`;
  };

  // Funktion zum Sortieren der Mitglieder alphabetisch
  const sortMembersAlphabetically = (members) => {
    return members.sort((a, b) => {
      return a.name.localeCompare(b.name); // Sortiere nach dem Namen der Mitglieder
    });
  };

  // Fetch Mitglieder und Auftritte von Firebase
  useEffect(() => {
    const fetchData = async () => {
      const membersSnapshot = await getDocs(collection(db, "members"));
      const membersList = membersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Den angemeldeten User in die erste Zeile setzen
      const sortedMembers = sortMembersAlphabetically(membersList);
      const userIndex = sortedMembers.findIndex((m) => m.id === user.uid);
      
      // Wenn der User gefunden wurde, setze ihn an die erste Stelle
      if (userIndex > -1) {
        const userMember = sortedMembers.splice(userIndex, 1); // Entferne den User
        sortedMembers.unshift(userMember[0]); // Füge den User an den Anfang
      }

      setMembers(sortedMembers);

      const perfSnapshot = await getDocs(collection(db, "performances"));
      const perfList = perfSnapshot.docs
        .map((doc) => ({
          id: doc.id,
          date: doc.data().date || "9999-12-31",
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      setPerformances(perfList);
    };

    fetchData();
  }, [user.uid]); // Stelle sicher, dass die Benutzer-ID in der useEffect-Dependencyliste enthalten ist

  // Funktion zum Hinzufügen eines Auftritts
  const addPerformance = async () => {
    if (!ADMIN_EMAILS.includes(user.email) || user.guest) return;

    if (!newPerformanceName || !newPerformanceDate) {
      alert("Bitte sowohl den Auftrittsnamen als auch das Datum angeben.");
      return;
    }

    // Speichern des neuen Auftritts in Firebase
    const perfRef = doc(db, "performances", newPerformanceName);
    await setDoc(perfRef, { date: newPerformanceDate });

    setPerformances([...performances, { id: newPerformanceName, date: newPerformanceDate }]);
    
    // Zurücksetzen der Eingabefelder
    setNewPerformanceName("");
    setNewPerformanceDate("");
  };

  return (
    <div className="p-6 bg-gray-100">
       <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Teilnahme-Tabelle</h2>

      {user.guest ? (
        <p className="text-center text-gray-600 mb-6">🔒 Gäste können die Tabelle nur ansehen!</p>
      ) : null}

      {ADMIN_EMAILS.includes(user.email) && !user.guest && (
        <div className="mb-4">
          <h3 className="text-xl font-semibold mb-2">Neuen Auftritt hinzufügen</h3>
          <div>
            <input
              type="text"
              placeholder="Auftrittsname"
              value={newPerformanceName}
              onChange={(e) => setNewPerformanceName(e.target.value)}
              className="p-2 border border-gray-300 rounded-md mb-2 w-full"
            />
            <input
              type="date"
              value={newPerformanceDate}
              onChange={(e) => setNewPerformanceDate(e.target.value)}
              className="p-2 border border-gray-300 rounded-md mb-4 w-full"
            />
            <button
              onClick={addPerformance}
              className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none w-full"
            >
              Auftritt hinzufügen
            </button>
          </div>
        </div>
      )}

      {/* Die Tabelle bleibt zentriert und ermöglicht Scrollen bei Bedarf */}
      <div className="max-w-full mx-auto overflow-x-auto">
        <table className="min-w-max table-auto border-collapse border border-gray-300 rounded-lg">
          <thead className="bg-gray-200">
            <tr>
              <th
                className="p-1 text-center bg-blue-100 sticky left-0 text-xs"
                style={{ zIndex: 10 }} // Fixiere die erste Spalte beim horizontalen Scrollen
              >
                Mitglied
              </th>
              {performances.map((perf, index) => (
                <th
                  key={perf.id}
                  className={`p-1 text-center ${index % 2 === 1 ? "bg-blue-100" : "bg-gray-100"} text-xs w-[20px]`}
                >
                  {perf.id}
                  <br />
                  <small>{formatDate(perf.date)}</small>
                </th>
              ))}
            </tr>
            <tr className="bg-red-700">
              <td
                className="p-0 text-center text-gray-100 sticky left-0 bg-red-700 text-xs"
                style={{ zIndex: 5 }} // Fixiere "Auswertung" beim horizontalen Scrollen
              >
                <strong>Auswertung</strong>
              </td>
              {performances.map((perf, index) => {
                let countB = 0;
                let countT = 0;

                members.forEach((m) => {
                  if (m[perf.id] === "ja") {
                    if (m.type === "B") countB++;
                    if (m.type === "T") countT++;
                  }
                });

                // Alternierende Farben für die Zellen
                const bgColor = index % 2 === 0 ? "bg-red-700" : "bg-red-500"; // Alternierende Farben

                return (
                  <td
                    key={perf.id}
                    className={`p-0 text-center text-gray-100 ${bgColor} text-sm`}
                  >
                    <strong> {countB}🎺 {countT}🥁 </strong>
                  </td>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {members.map((member) => (
              <tr key={member.id} className="hover:bg-gray-100">
                <td
                  className="p-0 text-center sticky left-0 bg-gray-100 text-xs w-[100px]"
                  style={{
                    zIndex: 5, // Fixiere den Mitgliedsnamen in der ersten Spalte
                  }}
                >
                  {member.name} ({member.type})
                </td>
                {performances.map((perf) => (
                  <td
                    key={perf.id}
                    className="p-1 text-center text-sm"
                    style={{
                      backgroundColor:
                        member[perf.id] === "ja"
                          ? "lightgreen"
                          : member[perf.id] === "vielleicht"
                          ? "yellow"
                          : member[perf.id] === "nein"
                          ? "lightcoral"
                          : "white",
                    }}
                  >
                    {!user.guest && user.uid === member.id ? (
                      <select
                        value={member[perf.id] || ""}
                        onChange={(e) =>
                          updateAvailability(member.id, perf.id, e.target.value)
                        }
                        className="border p-0 rounded text-sm"
                      >
                        <option value="">---</option>
                        <option value="ja">Ja</option>
                        <option value="vielleicht">Vielleicht</option>
                        <option value="nein">Nein</option>
                      </select>
                    ) : (
                      member[perf.id] ? <strong>{member[perf.id]}</strong> : "---"
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;
